/**
 * Gestione piani commerciali (SuperAdmin MAE).
 *
 * I 5 piani live oggi: FREE / STARTER / BASIC / PRO / ENTERPRISE.
 * Vista MaeDesign: catalogo a card (stile pricing) + KPI in cima, con la vista
 * tabellare completa sotto (ricerca/sort/export/paginazione via ResourceListPage).
 * Il pricing multi-valuta e i bundle items restano su license-pricings /
 * license-plan-items — qui esponiamo solo il catalogo e la creazione base.
 */
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import {
  IconCircleCheck, IconGift, IconLicense, IconLock, IconTarget, IconUsers, IconWorld,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { StatCard } from '@/shared/components/StatCard';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { QuickCreateButton, type FieldDef } from '@/shared/components/QuickCreateButton';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { fetchList } from '@/shared/api/jsonapi/client';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import type { LicensePlan } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/license-plans')({ component: LicensePlansPage });

type Row = LicensePlan & Denormalized & { id: string };

/** Metadati per tier: etichetta, colore badge (tema) e accento bordo-card. */
const TIER_META: Record<string, { label: string; color: string; accent: string }> = {
  free: { label: 'Free', color: 'gray', accent: mw.ink500 },
  starter: { label: 'Starter', color: 'azzurro', accent: mw.accent },
  basic: { label: 'Basic', color: 'cyan', accent: mw.accentText },
  pro: { label: 'Pro', color: 'bordeaux', accent: mw.brand },
  enterprise: { label: 'Enterprise', color: 'grape', accent: mw.brandDark },
};

/** Campi del form di creazione (riusati nel bottone di header). */
const CREATE_FIELDS: FieldDef[] = [
  { name: 'code', label: 'Codice (uppercase)', required: true, placeholder: 'STARTER' },
  { name: 'name', label: 'Nome commerciale', required: true },
  { name: 'description', label: 'Descrizione', kind: 'textarea' },
  { name: 'tier', label: 'Tier', kind: 'select', options: [
    { value: 'free', label: 'Free' },
    { value: 'starter', label: 'Starter' },
    { value: 'basic', label: 'Basic' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' },
  ] },
  { name: 'targetSegment', label: 'Segmento target' },
  { name: 'maxSeats', label: 'Max seats', kind: 'number' },
  { name: 'sortOrder', label: 'Ordine visualizzazione', kind: 'number', defaultValue: 100 },
  { name: 'isPubliclyVisible', label: 'Pubblicamente visibile', kind: 'boolean', defaultValue: true },
  { name: 'isTrialEligible', label: 'Eleggibile a trial', kind: 'boolean', defaultValue: false },
  { name: 'trialDurationDays', label: 'Giorni trial', kind: 'number' },
  { name: 'isActive', label: 'Attivo', kind: 'boolean', defaultValue: true },
];

/** Card catalogo in stile pricing: tier, codice, nome, capacità e stato. */
function PlanCard({ plan }: { plan: Row }) {
  const tier = (plan.tier ?? '').toLowerCase();
  const meta = TIER_META[tier] ?? { label: plan.tier ?? '—', color: 'gray', accent: mw.ink500 };
  return (
    <Card
      withBorder
      radius="lg"
      shadow="sm"
      padding="lg"
      style={{ borderLeft: `4px solid ${meta.accent}`, opacity: plan.isActive ? 1 : 0.68 }}
    >
      <Group justify="space-between" align="center" mb={8}>
        <Badge variant="light" color={meta.color}>{meta.label}</Badge>
        <Text style={{ ...mwSectionLabel, fontVariantNumeric: 'tabular-nums' }}>#{plan.sortOrder ?? 0}</Text>
      </Group>

      <Text
        style={{
          fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase',
          color: mw.ink300, fontVariantNumeric: 'tabular-nums',
        }}
      >
        {plan.code}
      </Text>
      <Text fw={700} fz="xl" style={{ color: mw.ink900, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
        {plan.name}
      </Text>
      {plan.description && (
        <Text size="sm" c="dimmed" mt={4} lineClamp={2}>{plan.description}</Text>
      )}

      <Group gap="lg" mt="md" wrap="wrap">
        <Group gap={6} wrap="nowrap">
          <IconUsers size={15} color={mw.ink500} />
          <Text size="sm" style={{ color: mw.ink900, fontVariantNumeric: 'tabular-nums' }}>
            {plan.maxSeats != null ? `${plan.maxSeats} seats` : 'Seats illimitati'}
          </Text>
        </Group>
        {plan.targetSegment && (
          <Group gap={6} wrap="nowrap">
            <IconTarget size={15} color={mw.ink500} />
            <Text size="sm" c="dimmed">{plan.targetSegment}</Text>
          </Group>
        )}
      </Group>

      <Group gap={6} mt="md" wrap="wrap">
        {plan.isActive
          ? <Badge variant="light" color="green" leftSection={<IconCircleCheck size={11} />}>Attivo</Badge>
          : <Badge variant="light" color="red">Inattivo</Badge>}
        {plan.isPubliclyVisible
          ? <Badge variant="light" color="green" leftSection={<IconWorld size={11} />}>Pubblico</Badge>
          : <Badge variant="light" color="gray" leftSection={<IconLock size={11} />}>Privato</Badge>}
        {plan.isTrialEligible && (
          <Badge variant="light" color="cyan" leftSection={<IconGift size={11} />}>
            Trial{plan.trialDurationDays ? ` ${plan.trialDurationDays}gg` : ''}
          </Badge>
        )}
      </Group>
    </Card>
  );
}

function LicensePlansPage() {
  // Catalogo per KPI + card. Chiave sotto il prefisso ['jsonapi','license-plans']
  // così l'invalidazione di QuickCreateButton (create) aggiorna anche queste card.
  const plansQ = useQuery({
    queryKey: ['jsonapi', 'license-plans', 'catalog'],
    queryFn: () => fetchList<Row>('license-plans', { sort: ['sortOrder'], page: { size: 100 } }),
  });
  const plans = plansQ.data?.data ?? [];

  const stats = {
    total: plans.length,
    active: plans.filter((p) => p.isActive).length,
    publicCount: plans.filter((p) => p.isPubliclyVisible).length,
    trial: plans.filter((p) => p.isTrialEligible).length,
  };

  const isMobile = useIsMobileShell();

  const kpi = plansQ.isLoading ? (
    <InlineLoader label="Caricamento dei piani licenza…" minHeight={120} />
  ) : (
    <SimpleGrid cols={{ base: 2, sm: 4 }}>
      <StatCard icon={IconLicense} color="brand" label="Piani totali" value={stats.total} />
      <StatCard icon={IconCircleCheck} color="green" label="Attivi" value={stats.active} />
      <StatCard icon={IconWorld} color="azzurro" label="Pubblici" value={stats.publicCount} />
      <StatCard icon={IconGift} color="cyan" label="Con trial" value={stats.trial} />
    </SimpleGrid>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        <MobileHeader title="Piani commerciali" />
        <Group justify="flex-end">
          <QuickCreateButton type="license-plans" title="Nuovo piano" buttonLabel="Nuovo piano" fields={CREATE_FIELDS} />
        </Group>
        {plansQ.isError && <QueryErrorAlert error={plansQ.error} onRetry={() => void plansQ.refetch()} />}
        {kpi}
        <div>
          <Text style={{ ...mwSectionLabel, marginBottom: 10 }}>Catalogo</Text>
          {plansQ.isLoading ? (
            <InlineLoader label="Caricamento dei piani licenza…" minHeight={120} />
          ) : plans.length === 0 ? (
            <Card withBorder radius="lg" shadow="sm" padding="xl">
              <Stack align="center" gap={6}>
                <IconLicense size={30} color={mw.ink300} />
                <Text fw={600}>Nessun piano commerciale</Text>
                <Text size="sm" c="dimmed">Crea il primo piano con «Nuovo piano».</Text>
              </Stack>
            </Card>
          ) : (
            <Stack gap={12}>
              {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
            </Stack>
          )}
        </div>
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconLicense size={22} />}
        title="Piani commerciali"
        sub="Catalogo dei piani vendibili. Bundle e pricing multi-valuta si gestiscono nelle risorse dedicate license-plan-items e license-pricings."
        actions={<QuickCreateButton type="license-plans" title="Nuovo piano" fields={CREATE_FIELDS} />}
      />

      <Stack>
        {plansQ.isError && <QueryErrorAlert error={plansQ.error} onRetry={() => void plansQ.refetch()} />}

        {plansQ.isLoading ? (
          <InlineLoader label="Caricamento dei piani licenza…" minHeight={120} />
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <StatCard icon={IconLicense} color="brand" label="Piani totali" value={stats.total} />
            <StatCard icon={IconCircleCheck} color="green" label="Attivi" value={stats.active} />
            <StatCard icon={IconWorld} color="azzurro" label="Pubblici" value={stats.publicCount} />
            <StatCard icon={IconGift} color="cyan" label="Con trial" value={stats.trial} />
          </SimpleGrid>
        )}

        <div>
          <Text style={{ ...mwSectionLabel, marginBottom: 10 }}>Catalogo</Text>
          {plansQ.isLoading ? (
            <InlineLoader label="Caricamento dei piani licenza…" minHeight={120} />
          ) : plans.length === 0 ? (
            <Card withBorder radius="lg" shadow="sm" padding="xl">
              <Stack align="center" gap={6}>
                <IconLicense size={30} color={mw.ink300} />
                <Text fw={600}>Nessun piano commerciale</Text>
                <Text size="sm" c="dimmed">Crea il primo piano con il bottone «Nuovo piano».</Text>
              </Stack>
            </Card>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
              {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
            </SimpleGrid>
          )}
        </div>

        <div>
          <Text style={{ ...mwSectionLabel, marginBottom: 10 }}>Vista tabellare</Text>
          <ResourceListPage<Row>
            title=""
            type="license-plans"
            enableSort
            searchField="name"
            columns={[
              { accessor: 'sortOrder', title: '#', width: 60 },
              { accessor: 'code', title: 'Codice', width: 140 },
              { accessor: 'name', title: 'Nome' },
              { accessor: 'tier', title: 'Tier', width: 110, render: (row) => {
                const v = (row.tier ?? '').toLowerCase();
                const meta = TIER_META[v];
                return v ? <Badge variant="light" size="sm" color={meta?.color ?? 'gray'}>{meta?.label ?? v}</Badge> : null;
              } },
              { accessor: 'targetSegment', title: 'Segmento', width: 140 },
              { accessor: 'maxSeats', title: 'Max seats', width: 100 },
              { accessor: 'isPubliclyVisible', title: 'Pubblico', width: 90, render: (row) => row.isPubliclyVisible ? <Badge color="green" size="xs">Sì</Badge> : <Badge color="gray" size="xs">No</Badge> },
              { accessor: 'isTrialEligible', title: 'Trial', width: 80, render: (row) => row.isTrialEligible ? <Badge color="cyan" size="xs">Sì</Badge> : null },
              { accessor: 'isActive', title: 'Attivo', width: 80, render: (row) => row.isActive ? <Badge color="green" size="xs">Sì</Badge> : <Badge color="red" size="xs">No</Badge> },
            ]}
            emptyMessage="Nessun piano commerciale"
            emptyDescription="Crea il primo piano con il bottone «Nuovo piano» in alto."
            emptyIcon={IconLicense}
          />
        </div>
      </Stack>
    </MaeShell>
  );
}
